import { formatOfferAmountLabel, type OfferDiscount } from '../new-client-offer/new-client-offer-discount';
import type { CommentAutomationDmButtonKind } from './comment-automation-dm-button-destinations';
import type { CommentAutomationPlatform } from './comment-automation-schemas';

/** The commenter's name in a Comment → DM message; the editor's "{name}" chip inserts it. */
export const DM_NAME_TOKEN = '{name}';

/**
 * The New Client Offer's reward ("$10 off" / "15% off") in a DM message, a button label or a public reply. Filled at
 * SEND time from the live offer, so changing the offer changes every next DM with no automation edit.
 */
export const DM_OFFER_TOKEN = '{offer}';

/** Used when Meta gives us no usable name for the commenter. */
export const DM_NAME_FALLBACK = 'there';

// Tolerates the casing/spacing people type by hand: {name}, {Name}, { name } (same for {offer}).
const NAME_TOKEN_PATTERN = /\{\s*name\s*\}/gi;
const OFFER_TOKEN_PATTERN = /\{\s*offer\s*\}/gi;
/** A pathological display name must not blow the DM past Meta's length limit. */
export const DM_NAME_MAX_CHARS = 30;
/** Longest `{offer}` text: "$499.99 off" (the offer is capped at $500 / 100%). */
export const DM_OFFER_MAX_CHARS = 11;

/** What a DM message using `{offer}` says instead while the salon has no live New Client Offer (DM with buttons). */
export const DM_OFFER_OFF_TEXT = 'Hi {name}! Thanks for your comment. Tap below to see our page and book.';
/** The same for a DM that goes out without buttons (text only, or every button was left out). */
export const DM_OFFER_OFF_TEXT_NO_BUTTONS = 'Hi {name}! Thanks for your comment. Reply here and we’ll help you book.';
/**
 * What a button label using `{offer}` says instead, by where the button opens: the offer button already falls back to
 * the Bio Page → booking, so it and the Bio Page say "See our page"; booking says "Book now"; a campaign or custom
 * link "Learn more".
 */
export const DM_OFFER_OFF_BUTTON_LABELS: Record<CommentAutomationDmButtonKind, string> = {
  NEW_CLIENT_OFFER: 'See our page',
  BIO_PAGE: 'See our page',
  ONLINE_BOOKING: 'Book now',
  CAMPAIGN: 'Learn more',
  CUSTOM: 'Learn more',
};
/** The public reply when every reply variant uses `{offer}` and no offer is live. */
export const DM_OFFER_OFF_PUBLIC_REPLY = 'Thanks! Check your messages.';

/**
 * What `{name}` becomes for a commenter: the first word of a Facebook display name ("Jenny Nguyen" → "Jenny"),
 * or the Instagram username as-is (IG comment webhooks carry only the username). Falls back to "there".
 */
export function resolveDmRecipientName(
  platform: CommentAutomationPlatform,
  commenterName: string | null | undefined
): string {
  const trimmed = commenterName?.trim() ?? '';
  const name = platform === 'FACEBOOK' ? (trimmed.split(/\s+/)[0] ?? '') : trimmed;
  return name ? Array.from(name).slice(0, DM_NAME_MAX_CHARS).join('') : DM_NAME_FALLBACK;
}

/** Fills `{name}` in the DM template; a blank name becomes "there" ("Hi there!"). */
export function renderDmText(template: string, vars: { name: string | null | undefined }): string {
  const name = vars.name?.trim() || DM_NAME_FALLBACK;
  // Replacer function, not a string: a name like "A$'" must never be read as a `$` replacement pattern.
  return template.replace(NAME_TOKEN_PATTERN, () => name);
}

/**
 * Length of the DM once sent, assuming every `{name}` expands to the longest allowed name and every `{offer}` to the
 * longest reward. Validation and the editor's "98 / 640" counter both use it, so a template that fits can never exceed
 * Meta's limit when sent.
 */
export function dmTextWorstCaseLength(template: string): number {
  return offerTextWorstCaseLength(template.replace(NAME_TOKEN_PATTERN, () => 'x'.repeat(DM_NAME_MAX_CHARS)));
}

/** Length of a button label / public reply once sent, with every `{offer}` at its longest ("$499.99 off"). */
export function offerTextWorstCaseLength(text: string): number {
  return text.replace(OFFER_TOKEN_PATTERN, () => 'x'.repeat(DM_OFFER_MAX_CHARS)).length;
}

export function hasDmOfferToken(text: string): boolean {
  // A fresh regex: the shared /g pattern keeps `lastIndex` between `.test` calls.
  return /\{\s*offer\s*\}/i.test(text);
}

/** "$10 off" / "$10.50 off" / "15% off" — what `{offer}` becomes for a live offer. */
export function resolveOfferRewardText(offer: OfferDiscount): string {
  return `${formatOfferAmountLabel(offer)} off`;
}

/**
 * `{offer}` filled in a DM message, button label or public reply. `offerReward` = the live offer's reward
 * (`resolveOfferRewardText`), null while no offer is live: text without the token is returned unchanged, text with it
 * becomes `whenOff` (null = drop it).
 */
function fillOfferToken<T extends string | null>(text: string, offerReward: string | null, whenOff: T): string | T {
  if (!hasDmOfferToken(text)) return text;
  if (offerReward === null) return whenOff;
  return text.replace(OFFER_TOKEN_PATTERN, () => offerReward);
}

/**
 * The DM message for the live offer; with no offer live a message promising one becomes the fixed "see our page" line
 * (`hasButtons`: the buttons actually sent) or, without buttons, the "reply here" line.
 */
export function resolveDmTextForOffer(template: string, offerReward: string | null, options: { hasButtons: boolean }): string {
  return fillOfferToken(template, offerReward, options.hasButtons ? DM_OFFER_OFF_TEXT : DM_OFFER_OFF_TEXT_NO_BUTTONS);
}

/** A button label for the live offer; with no offer live a label promising one says where the button opens instead. */
export function resolveDmButtonLabelForOffer(
  label: string,
  offerReward: string | null,
  kind: CommentAutomationDmButtonKind
): string {
  return fillOfferToken(label, offerReward, DM_OFFER_OFF_BUTTON_LABELS[kind]);
}

/**
 * The public reply variants for the live offer. With no offer live, variants using `{offer}` are left out; when that
 * leaves none (but there were some), the neutral `DM_OFFER_OFF_PUBLIC_REPLY` is used.
 */
export function resolvePublicRepliesForOffer(variants: readonly string[], offerReward: string | null): string[] {
  const usable = variants.map((variant) => variant.trim()).filter(Boolean);
  const filled = usable.flatMap((variant) => {
    const text = fillOfferToken(variant, offerReward, null);
    return text === null ? [] : [text];
  });
  return filled.length === 0 && usable.length > 0 ? [DM_OFFER_OFF_PUBLIC_REPLY] : filled;
}

/** Lead source label for a sign-up that came from a Comment → DM button: "Instagram · Comment DM: Fall promo". */
export function commentDmLeadSourceLabel(platform: CommentAutomationPlatform, automationName: string): string {
  return `${platform === 'INSTAGRAM' ? 'Instagram' : 'Facebook'} · Comment DM: ${automationName}`;
}
